<script setup>
import {
    computed,
    inject,
    onBeforeUnmount,
    onBeforeUpdate,
    onMounted,
    ref,
    watch,
} from "vue";
import { router } from "@inertiajs/vue3";
import { useImageUpload } from "@/composables/useImageUpload";
import { useToastStore } from "@/stores/toast.js";
import { useModalogStore } from "@/stores/modalog";
import ImageUpload from "@/Components/Actions/ImageUpload.vue";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";
import CoverFocusPicker from "@/Components/Profile/CoverFocusPicker.vue";
const route = inject("route");
import * as Sentry from "@sentry/vue";

const {
    dropzoneRef,
    fetchUploadSignature,
    file,
    form,
    imageAdded,
    imageUploaded,
    resetDropzone,
    uploadImage,
} = useImageUpload();
const modalogStore = useModalogStore();
const toastStore = useToastStore();

const props = defineProps({
    list: {
        type: Object,
        default: function () {
            return {};
        },
    },
});

const saving = ref(false);

// --- Focal point (BH-38) ---
// Null means "let Cloudinary pick" (auto gravity); otherwise {x, y} as
// percentages of the image.
function savedFocus() {
    return Number.isFinite(props.list.masthead_focus_x) &&
        Number.isFinite(props.list.masthead_focus_y)
        ? { x: props.list.masthead_focus_x, y: props.list.masthead_focus_y }
        : null;
}

const focus = ref(savedFocus());
const pickerValue = computed(() => focus.value ?? { x: 50, y: 50 });

const focusChanged = computed(() => {
    const saved = savedFocus();

    if (!saved || !focus.value) {
        return Boolean(saved) !== Boolean(focus.value);
    }

    return saved.x !== focus.value.x || saved.y !== focus.value.y;
});

// A queued file previews from an object URL; otherwise the current cover
// previews from Cloudinary, wide enough for the picker.
const objectUrl = ref(null);

watch(
    file,
    (queued) => {
        if (objectUrl.value) {
            URL.revokeObjectURL(objectUrl.value);
            objectUrl.value = null;
        }

        if (queued) {
            objectUrl.value = URL.createObjectURL(queued);
            // A fresh photo starts from the middle, not the old photo's point.
            focus.value = { x: 50, y: 50 };
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    if (objectUrl.value) {
        URL.revokeObjectURL(objectUrl.value);
    }
});

const previewSrc = computed(() => {
    if (objectUrl.value) {
        return objectUrl.value;
    }

    return props.list.masthead_photo
        ? `https://res.cloudinary.com/gift-hero/image/upload/c_limit,w_680,q_auto:good/${props.list.masthead_photo}.jpg`
        : null;
});

// Save needs either a new photo or a moved focus point on the current one.
const canSave = computed(
    () =>
        Boolean(file.value) ||
        (Boolean(props.list.masthead_photo) && focusChanged.value),
);

function resetFocus() {
    focus.value = null;
}

// The host keeps this instance mounted between opens (stable-root
// pattern). The dropzone itself remounts fresh with the shell, but the
// composable's queued file survives — clear it so Save re-disables.
watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            file.value = null;
            focus.value = savedFocus();
        }
    },
);

onMounted(async () => {
    await fetchUploadSignature("list_cover_photo");
});

onBeforeUpdate(async () => {
    await fetchUploadSignature("list_cover_photo");
});

const openRemoveWishListImageModalog = () => {
    modalogStore.showModalog({
        type: "ModalogConfirmRemoveWishlistImage",
        props: {
            list: props.list,
        },
        isConfirmDisabled: false,
    });
};

const saveWishListImage = () => {
    // The upload form only knows its initial keys; ride the focus along.
    form.transform((data) => ({
        ...data,
        focus_x: focus.value?.x ?? null,
        focus_y: focus.value?.y ?? null,
    }));

    form.post(
        route("giftlists.update_photo", {
            giftlist: props.list.uuid,
        }),
        {
            onSuccess: () => {
                saving.value = false;
                modalogStore.closeModalog();
                toastStore.success("Your list's cover photo was updated.");
                resetDropzone();
                router.reload({
                    force: true,
                    forceFormData: true,
                    replace: true,
                });
            },
            onError: () => {
                saving.value = false;
                toastStore.error(
                    "Something went wrong saving your cover photo.",
                );
            },
        },
    );
};

const uploadAndSave = async () => {
    saving.value = true;

    // No new file: just move the focus point on the current cover.
    if (!file.value) {
        form.asset_id = null;
        form.public_id = props.list.masthead_photo;
        saveWishListImage();

        return;
    }

    try {
        // A fresh signature right before the upload: the one fetched on
        // mount may still be in flight if Save follows the drop quickly.
        await fetchUploadSignature("list_cover_photo");
        const responseData = await uploadImage();
        form.asset_id = responseData.asset_id;
        form.public_id = responseData.public_id;
        saveWishListImage();
    } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        saving.value = false;
        toastStore.error("Something went wrong uploading your cover photo.");
    }
};
</script>

<template>
    <BrandConfirmModalog
        :title="
            props.list.masthead_photo
                ? 'Change Header Photo'
                : 'Upload Header Photo'
        "
        confirm-label="Save"
        busy-label="Saving"
        :busy="saving"
        :confirm-disabled="!canSave"
        @confirm="uploadAndSave"
    >
        <ImageUpload
            ref="dropzoneRef"
            @image-added="imageAdded"
            @image-uploaded="imageUploaded"
        />

        <!-- Focus point (BH-38): for the queued photo, or the current one -->
        <div v-if="previewSrc" class="mt-5" data-test="focus-section">
            <div class="mb-2 flex items-center justify-between gap-4">
                <p class="text-[14px] font-semibold text-[#3c3938]">
                    Tap the photo to choose what stays in view.
                </p>
                <button
                    v-if="focus"
                    type="button"
                    class="shrink-0 text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                    data-test="focus-reset"
                    @click="resetFocus"
                >
                    Reset to automatic
                </button>
            </div>
            <CoverFocusPicker
                :model-value="pickerValue"
                :src="previewSrc"
                :alt="props.list.name ? `${props.list.name} cover` : 'Cover'"
                @update:model-value="focus = $event"
            />
        </div>
        <div v-if="props.list.masthead_photo" class="mt-4 text-center">
            <button
                type="button"
                class="text-[14px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                @click.stop="openRemoveWishListImageModalog"
            >
                Remove current header photo
            </button>
        </div>
    </BrandConfirmModalog>
</template>
